/**
 * Сердце проекта: профиль → плоский набор признаков для условных блоков шаблона.
 *
 * Здесь НЕТ ни одной правовой формулировки. Формулировки живут в шаблоне .docx
 * и правятся юристом в Word (ТЗ п. 6.3, требование к реализации после таблицы правил).
 * Здесь только условия из таблицы правил R-01…R-30, покрытые тестами.
 */

import type { CaseProfile } from '../types/profile.js';
import { computeTotals } from './totals.js';
import { DEBT_THRESHOLD_KOPECKS } from './constants.js';

/**
 * Флаги условных блоков. Имя флага = имя условия в шаблоне:
 * `{#has_property}` … `{/has_property}`, отрицание — `{^has_property}`.
 */
export interface BranchingFlags {
  // --- Имущество (R-11, R-12, R-15, R-16) ---
  has_property: boolean;

  // --- Сделки (R-13, R-14) ---
  has_deals: boolean;

  // --- Правовое основание обращения (R-09, R-10) ---
  /** Сумма ≥ 500 000 ₽ И есть просрочка свыше трёх месяцев — обязанность должника */
  ground_obligation: boolean;
  /** Иначе — право должника, предвидение банкротства */
  ground_right: boolean;
  debt_below_threshold: boolean;
  has_overdue: boolean;

  // --- Участие в организациях (R-17, R-18) ---
  has_organizations: boolean;

  // --- Брак (R-19…R-22) ---
  is_single: boolean;
  is_married: boolean;
  is_divorced: boolean;
  /** Выбраны оба статуса — выводятся обе формулировки подряд */
  married_and_divorced: boolean;

  // --- Дети (R-23, R-24) ---
  has_children: boolean;
  has_dependent_children: boolean;

  // --- Занятость (R-25, R-26) ---
  is_employed: boolean;

  // --- Социальные выплаты (R-27, R-28) ---
  has_social_payments: boolean;

  // --- Прочее ---
  is_entrepreneur: boolean;
  has_representative: boolean;
  /** Пол должника — для согласования рода (FR-37) */
  is_female: boolean;
  is_male: boolean;
  /** Заполнена регистрация по месту жительства (FR-23) */
  has_registration: boolean;
  // --- Разделы описи: непустые (для служебных отметок, не для формы) ---
  has_realty: boolean;
  has_vehicles: boolean;
  has_accounts: boolean;
  has_shares: boolean;
  has_securities: boolean;
  has_valuables: boolean;
  has_obligatory_payments: boolean;
}

export function computeBranching(profile: CaseProfile): BranchingFlags {
  const totals = computeTotals(profile);
  const family = profile.family;
  const property = profile.property;
  const income = profile.income;

  const isMarried = family.statuses.includes('married');
  const isDivorced = family.statuses.includes('divorced');

  const aboveThreshold = totals.total >= DEBT_THRESHOLD_KOPECKS;
  const hasOverdue = totals.hasOverdueOverThreeMonths;

  /*
   * R-10: сумма ≥ порога И просрочка свыше трёх месяцев → обоснование
   *       строится на обязанности должника (ст. 213.4, п. 2 ст. 213.3).
   * R-09: во всех остальных случаях → на праве должника
   *       (п. 2 ст. 213.4 и п. 11 постановления Пленума ВС РФ № 45).
   * Ветки взаимоисключающие: заявление не может ссылаться сразу на обе.
   */
  const groundObligation = aboveThreshold && hasOverdue;

  return {
    has_property: property.hasProperty,

    has_deals: profile.deals.hasDeals && profile.deals.items.length > 0,

    ground_obligation: groundObligation,
    ground_right: !groundObligation,
    debt_below_threshold: !aboveThreshold,
    has_overdue: hasOverdue,

    has_organizations: income.hasOrganizations && income.organizations.length > 0,

    is_single: family.statuses.includes('single') && !isMarried && !isDivorced,
    is_married: isMarried,
    is_divorced: isDivorced,
    married_and_divorced: isMarried && isDivorced,

    has_children: family.children.length > 0,
    has_dependent_children: family.children.some((c) => c.dependent),

    is_employed: income.employed,
    has_social_payments: income.hasSocialPayments,
    is_entrepreneur: income.isEntrepreneur,
    has_representative: profile.representative.enabled,

    is_female: profile.debtor.gender === 'female',
    is_male: profile.debtor.gender !== 'female',

    has_registration: profile.debtor.hasRegistration,

    has_realty: property.realty.length > 0,
    has_vehicles: property.vehicles.length > 0,
    has_accounts: property.accounts.length > 0,
    has_shares: property.shares.length > 0,
    has_securities: property.securities.length > 0,
    has_valuables: property.valuables.length > 0,
    has_obligatory_payments: profile.obligatoryPayments.length > 0,
  };
}

/** Перечень имён флагов — используется проверкой шаблона (FR-42). */
export const BRANCHING_FLAG_NAMES: Array<keyof BranchingFlags> = [
  'has_property', 'has_deals',
  'ground_obligation', 'ground_right', 'debt_below_threshold', 'has_overdue',
  'has_organizations', 'is_single', 'is_married', 'is_divorced', 'married_and_divorced',
  'has_children', 'has_dependent_children', 'is_employed', 'has_social_payments',
  'is_entrepreneur', 'has_representative', 'is_female', 'is_male',
  'has_registration',
  'has_realty', 'has_vehicles', 'has_accounts', 'has_shares', 'has_securities',
  'has_valuables', 'has_obligatory_payments',
];
